import { useCallback, useEffect, useRef, useState } from "react";

import { uploadFile } from "../../../services/upload";

export type UseAttachedFilesOptions = {
  onUploadError?: (error: unknown) => void;
};

export type AttachmentItem = {
  id: string;
  file: File;
  status: "pending" | "done" | "error";
  attachment?: {
    key: string;
    source: string;
    type: "image" | "video" | "file";
    name?: string;
  };
};

function deriveAttachmentType(file: File): "image" | "video" | "file" {
  if (file.type.startsWith("image/")) return "image";
  if (file.type.startsWith("video/")) return "video";
  return "file";
}

export function useAttachedFiles(options: UseAttachedFilesOptions = {}) {
  const { onUploadError } = options;
  const [attachedItems, setAttachedItems] = useState<AttachmentItem[]>([]);
  /**
   * Map of uploaded-attachment S3 key → browser blob URL pointing at the
   * original local File. Kept across clearAll() so that chat bubbles for
   * messages the user just sent can preview the video *from the local
   * file* instead of hitting the CDN — the raw upload URL is revoked by
   * the ingest pipeline once transcoding finishes and returns 403, so
   * the remote preview would otherwise never resolve during the same
   * session. Revoked en masse when the hook unmounts.
   */
  const [localPreviewUrls, setLocalPreviewUrls] = useState<
    Record<string, string>
  >({});
  const localPreviewUrlsRef = useRef<Record<string, string>>({});
  localPreviewUrlsRef.current = localPreviewUrls;

  useEffect(() => {
    return () => {
      Object.values(localPreviewUrlsRef.current).forEach((url) => {
        try {
          URL.revokeObjectURL(url);
        } catch {
          // Best-effort cleanup — a revoke failure here would only leak
          // a browser-held blob for the rest of the page's lifetime.
        }
      });
    };
  }, []);

  const addFiles = useCallback(
    (files: File[]) => {
      const newItems: AttachmentItem[] = files.map((file) => ({
        id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        file,
        status: "pending" as const,
      }));
      setAttachedItems((prev) => [...prev, ...newItems]);

      newItems.forEach((item) => {
        uploadFile(item.file)
          .then((r) => {
            // Register a blob URL so the sent-message preview can render
            // from the local file. Only bother for media types that
            // actually have inline previews — skip generic files.
            const type = deriveAttachmentType(item.file);
            if (
              (type === "video" || type === "image") &&
              typeof URL !== "undefined" &&
              typeof URL.createObjectURL === "function"
            ) {
              try {
                const blobUrl = URL.createObjectURL(item.file);
                setLocalPreviewUrls((prev) => ({ ...prev, [r.key]: blobUrl }));
              } catch {
                // Non-fatal — we just lose the local preview shortcut and
                // fall back to the remote URL resolution path.
              }
            }

            setAttachedItems((prev) =>
              prev.map((p) =>
                p.id === item.id
                  ? {
                      ...p,
                      status: "done" as const,
                      attachment: {
                        key: r.key,
                        source: r.location,
                        type,
                        name: p.file.name,
                      },
                    }
                  : p,
              ),
            );
          })
          .catch((err) => {
            onUploadError?.(err);
            setAttachedItems((prev) =>
              prev.map((p) =>
                p.id === item.id ? { ...p, status: "error" as const } : p,
              ),
            );
          });
      });
    },
    [onUploadError],
  );

  const addFile = useCallback(
    (file: File) => {
      addFiles([file]);
    },
    [addFiles],
  );

  const removeFile = useCallback((index: number) => {
    setAttachedItems((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const clearAll = useCallback(() => {
    setAttachedItems([]);
  }, []);

  const readyAttachments = attachedItems
    .filter((item) => item.status === "done" && item.attachment)
    .map((item) => item.attachment!);

  const hasPending = attachedItems.some((item) => item.status === "pending");
  const hasError = attachedItems.some((item) => item.status === "error");

  return {
    attachedItems,
    addFiles,
    addFile,
    removeFile,
    clearAll,
    readyAttachments,
    hasPending,
    hasError,
    /**
     * Map of attachment S3 key → local blob URL for previewing freshly
     * uploaded images/videos without touching the network.
     */
    localPreviewUrls,
  };
}
